import React, {
  DragEvent,
  useContext,
  useEffect,
  useRef,
  useState,
  FormEvent,
} from 'react';
import { grey } from '@mui/material/colors';
import { Trans, useTranslation } from 'react-i18next';
import { UploadFile } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { InjectionSymbols, LogType, Logger } from '@hitiq/logger';
import { Box, Grid, Stack, Typography, useTheme } from '@mui/material';
import { di } from '../../state/inversify.config';
import styles from './bulkUploader.module.scss';
import { UserNotificationContext } from '../userNotification/userNotificationContext';
import { UserNotificationType } from '../userNotification/userNotificationType.enum';
import { Button } from '../button/button';
import { Checkbox } from '../checkBox/checkBox';

// Define type for object with key-value pairs representing cell Id and value of header row cells
export type HeaderRowCellObject = { cellId: string; cellValue: string };

export interface BulkUploaderTemplateFile {
  VERSION_NUMBER: string;
  VERSION_NUMBER_CELL_ID: string;
  HEADER_ROW_INDEX: number;
  HEADER_ROW_CELLS: HeaderRowCellObject[];
}
export interface BulkUploaderProps<T, R> {
  dataMapper: (data: R[]) => Promise<T[]>;
  validator: (data: T[]) => Promise<string[]>;
  uploader: (data: T[]) => Promise<string[]>;
  templateFile: BulkUploaderTemplateFile;
  onCancel?: () => void;
  setShowUploadCSVModal: (value: boolean) => void;
}

const allowedFileTypeMap = {
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

// R - raw input from file
// T - mapped data
export const BulkUploader = <
  T extends Record<string, string | number>,
  R extends Record<string, string | number>,
>({
  dataMapper,
  validator,
  uploader,
  templateFile,
  onCancel,
  setShowUploadCSVModal,
}: BulkUploaderProps<T, R>) => {
  const { t } = useTranslation();
  const [droppingFiles, setDroppingFiles] = useState(false);
  const { addUserNotification } = useContext(UserNotificationContext);
  const [fileName, setFileName] = useState('');
  const [dropzoneMessage, setDropZoneMessage] = useState('');
  const [result, setResult] = useState<string>();
  const theme = useTheme();
  const [validation, setValidation] = useState<{
    valid: boolean;
    message?: string;
    data?: T[];
  }>();
  const [uploadFiles, setUploadFiles] = useState<File | null>(null);
  const [isConsentConfirmed, setIsConsentConfirmed] = useState(false);

  useEffect(() => {
    if (validation?.valid) {
      uploadFile().catch((r) => {
        console.error(r);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validation]);

  const logger = di.get<Logger>(InjectionSymbols.LOGGER);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (): void => {
    setValidation({
      valid: false,
      message: '',
    });
    setResult('');
    setFileName('');

    const fileInput = uploadFiles;

    let file: File | null = null;

    if (fileInput) {
      file = fileInput;
    }

    if (!file) {
      // Reset the file input value to allow selecting the same file again
      if (fileInput) {
        setUploadFiles(null);
        fileInputRef.current = null;
      }
      setValidation({
        valid: false,
        message: `${t('bulkupload.BULK_UPLOAD_EMPTY_FILE')}`,
      });
      return;
    }
    // const file: File = files;

    // validate file type
    if (!Object.values(allowedFileTypeMap).includes(file.type)) {
      setValidation({
        valid: false,
        message: `${t('bulkupload.BULK_UPLOAD_FILE_TYPE_ERROR')}`,
      });
      return;
    }

    // validate file size
    if (file.size > 5000000) {
      setValidation({
        valid: false,
        message: `${t('bulkupload.BULK_UPLOAD_FILE_SIZE_ERROR')}`,
      });
      return;
    }

    // file reader logic
    const reader = new FileReader();

    reader.onload = async (event) => {
      const data = event.target?.result;
      if (typeof data === 'string') {
        const workBook = XLSX.read(data, {
          type: 'binary',
          dateNF: 'dd/mm/yyyy',
        });
        const sheetName = workBook.SheetNames[0];
        const workSheet = workBook.Sheets[sheetName];

        // Check file version is correct, return early if not
        if (
          workSheet[templateFile.VERSION_NUMBER_CELL_ID].v !==
          templateFile.VERSION_NUMBER
        ) {
          setValidation({
            valid: false,
            message: `${t('bulkupload.BULK_UPLOAD_INCORRECT_VERSION', {
              expectedTemplateVersion: [templateFile.VERSION_NUMBER],
            })}`,
          });
          return;
        }

        const dataRange: XLSX.Range = XLSX.utils.decode_range(
          workSheet['!ref'] as string,
        );
        // Skip the first n rows - target data we are interested in
        dataRange.s.r += templateFile.HEADER_ROW_INDEX;

        const jsonData: R[] = XLSX.utils.sheet_to_json<R>(workSheet, {
          range: dataRange,
        });

        // Return early if there is no data in the file that we are interested in
        if (!jsonData.length) {
          setValidation({
            valid: false,
            message: `${t('bulkupload.BULK_UPLOAD_EMPTY_FILE')}`,
          });
          return;
        }

        // Check the column headers match expected definitions
        const colHeadersMismatch = templateFile.HEADER_ROW_CELLS.find(
          ({ cellId, cellValue }) => {
            if (
              workSheet[cellId] === undefined ||
              workSheet[cellId].v !== cellValue
            ) {
              setValidation({
                valid: false,
                message: `${t(
                  'bulkupload.BULK_UPLOAD_INCORRECT_FORMAT_COL_HEADERS',
                )}`,
              });
              return true;
            }

            return false;
          },
        );

        if (colHeadersMismatch) {
          return;
        }

        const mappedData = dataMapper ? await dataMapper(jsonData) : [];

        // Custom validator if there is one passed in
        if (validator) {
          const errorMessages: string[] = await validator(mappedData);
          if (errorMessages.length) {
            const errorMessagesFormatted: string = errorMessages.join('<br>');
            setValidation({
              valid: false,
              message: `${t('bulkupload.BULK_UPLOAD_VALIDATION_ERROR', {
                errorMessages: errorMessagesFormatted,
              })}`,
            });
            return;
          }
        }

        // All validations passed
        setValidation({
          valid: true,
          message: `${t('bulkupload.BULK_UPLOAD_VALID_FILE', {
            fileName: file?.name,
            rowCount: mappedData.length,
          })}`,
          data: mappedData,
        });
      }
    };

    // start reading file if file valid and present
    reader.readAsBinaryString(file);

    // Reset the file input value to allow selecting the same file again
    if (fileInput) {
      // fileInput.value = '';
      setUploadFiles(null);
    }
  };

  const uploadFile = async () => {
    if (!validation?.data) {
      return;
    }

    try {
      const uploadMessages: string[] = await uploader(validation?.data);
      logger.log(LogType.info, `File uploaded successfully`);
      setDroppingFiles(false);
      if (
        validation.message &&
        !uploadMessages[0].includes('player already exists')
      ) {
        addUserNotification(
          UserNotificationType.SUCCESS,
          validation.message,
          UserNotificationType.SUCCESS,
        );
      }
      setResult(uploadMessages.join('<br>'));
    } catch (error) {
      if (validation.message) {
        addUserNotification(
          UserNotificationType.ERROR,
          validation.message,
          UserNotificationType.ERROR,
        );
      }
      logger.log(LogType.error, `Error uploading file ${error}`);
      fileInputRef.current = null;
    }
  };

  const allowDropFiles = (e: DragEvent<HTMLLabelElement>) => {
    setDroppingFiles(true);
    e.preventDefault();
  };

  const onDragLeave = (e: DragEvent<HTMLLabelElement>) => {
    setDroppingFiles(false);
    e.preventDefault();
  };

  const onUploading = (e: FormEvent<HTMLInputElement>) => {
    e.preventDefault();
    const target = e.target as HTMLInputElement;
    const addFiles: FileList | null = (fileInputRef.current as HTMLInputElement)
      ?.files;
    setDroppingFiles(false);
    if (addFiles) {
      const file: File | null = addFiles[0];
      setFileName(file?.name);
      // fileInputRef.current = file;
      setUploadFiles(file);
      setValidation(undefined);
    }
    target.value = '';
  };

  const onDropFile = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    const dt: FileList | null = e.dataTransfer?.files;
    if (dt) {
      const file: File | null = dt[0];
      setFileName(file?.name);
      setUploadFiles(file);
      setValidation(undefined);
    }
  };

  useEffect(() => {
    if (droppingFiles) {
      setDropZoneMessage(t('DROPPING_FILES'));
    } else {
      setDropZoneMessage(t('DRAG_AND_DROP_TITLE'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [droppingFiles]);

  useEffect(() => {
    if (
      validation &&
      validation?.valid &&
      !result?.includes('player already exists')
    ) {
      setShowUploadCSVModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  return (
    <Box
      sx={{
        maxWidth: '568px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Grid
        container
        component="label"
        onDrop={onDropFile}
        onDragOver={allowDropFiles}
        onDragLeaveCapture={onDragLeave}
        direction="column"
        alignItems="center"
        sx={{
          cursor: 'pointer',
          boxSizing: 'border-box',
          p: '16px 0px 32px',
          gap: '24px',
          mr: '13px',

          background: grey[200],
          border: `1px dashed ${theme.palette.primary.contrastText}`,
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '175%',
            letterSpacing: '0.15px',
            color: theme.palette.primary.main,
            fontFamily: 'Roboto',
            fontStyle: 'normal',
          }}
        >
          {dropzoneMessage}
        </Box>
        <Box>
          <UploadFile fontSize="large" />
        </Box>
        <Typography>{fileName}</Typography>
        <Box
          component="input"
          accept={Object.keys(allowedFileTypeMap).join(',')}
          onChange={onUploading}
          ref={fileInputRef}
          type="file"
          sx={{
            clip: 'rect(0 0 0 0)',
            clipPath: 'inset(50%)',
            height: 1,
            overflow: 'hidden',
            position: 'absolute',
            bottom: 0,
            left: 0,
            whiteSpace: 'nowrap',
            width: 1,
          }}
        />
      </Grid>
      {validation && (
        <div className={styles.uploadValidation}>
          {!result?.includes('player already exists') && (
            <Typography
              sx={{ mt: 1 }}
              className={
                validation?.valid
                  ? styles.uploadValidationSuccess
                  : styles.uploadValidationError
              }
            >
              <Trans
                i18nKey="bulkupload.BULK_UPLOAD_VALIDATION_MESSAGE_HOLDER"
                values={{ message: validation?.message }}
              />
            </Typography>
          )}
          <Typography
            sx={{ mt: 1 }}
            className={
              validation?.valid
                ? styles.uploadValidationSuccess
                : styles.uploadValidationError
            }
          >
            <Trans
              i18nKey="bulkupload.BULK_UPLOAD_VALIDATION_MESSAGE_HOLDER"
              values={{ message: result }}
            />
          </Typography>
        </div>
      )}
      <Grid sx={{ my: 2 }}>
        <Checkbox
          label={t('bulkupload.BULK_UPLOAD_CONSENT')}
          name="confirmUpload"
          checked={isConsentConfirmed}
          onChange={() => setIsConsentConfirmed(!isConsentConfirmed)}
        />
      </Grid>

      <Stack direction="row" spacing={2} justifyContent="right">
        {onCancel && (
          <Button
            label={t('CANCEL')}
            variant="contained"
            onClick={onCancel}
            type="button"
            color="primary"
          />
        )}
        <Button
          label={t('UPLOAD_BUTTON')}
          variant="contained"
          onClick={handleFileChange}
          type="button"
          color="primary"
          disabled={!isConsentConfirmed}
        />
      </Stack>
    </Box>
  );
};
